import { useEffect, useState } from 'react';
import { useAuth } from 'oidc-react';
import api from '../../components/services/apiService';
import Button from '../../components/Button';
import { getDashboardTenantsForGlobalAdmin, getTenantDataTypes } from '../../components/services/TenantSettingsService';
import { useToast } from '../../providers/ToastProvider/ToastContext';

interface ProcessorType {
  value: string;
  label: string;
}

interface TenantOption {
  id: string;
  name: string;
}

const formatProcessorLabel = (type: string) =>
  type
    .replace(/^Tenant/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim();

const DataRefreshTab = () => {
  const auth = useAuth();
  const { addToast } = useToast();
  const role = auth?.userData?.profile?.role ?? '';
  const isGlobalAdmin = role === 'globaladmin';

  const [processorTypes, setProcessorTypes] = useState<ProcessorType[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedProcessor, setSelectedProcessor] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [loading, setLoading] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  useEffect(() => {
    if (!isGlobalAdmin) {
      setProcessorTypes([]);
      setTenants([]);
      setIsLoadingOptions(false);
      return;
    }

    const loadOptions = async () => {
      setIsLoadingOptions(true);

      try {
        const [types, tenantList] = await Promise.all([getTenantDataTypes(), getDashboardTenantsForGlobalAdmin()]);

        setProcessorTypes(types.map((type) => ({ value: type, label: formatProcessorLabel(type) })));
        setTenants(tenantList.map((tenant) => ({ id: tenant.tenantId, name: tenant.tenantName })));
      } catch {
        setProcessorTypes([]);
        setTenants([]);
        addToast('Failed to load data processing options', 'error');
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadOptions();
  }, [addToast, isGlobalAdmin]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedProcessor || !selectedTenant) {
      addToast('Select a processor type and a tenant', 'error');
      return;
    }

    setLoading(true);

    try {
      await api.post(`/tenantservice/TenantDataProcessor/${selectedTenant}/process/${selectedProcessor}`);

      addToast('Process started successfully', 'success');
    } catch {
      addToast('Failed to start process', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!isGlobalAdmin) {
    return (
      <div
        style={{
          border: '1px solid #474646',
          borderRadius: '12px',
          padding: '24px',
          color: '#fff',
          background: 'rgba(255, 255, 255, 0.02)',
        }}
      >
        <h2 style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Process Data</h2>
        <p style={{ color: '#B9B9B9', margin: 0 }}>This action is available only for global administrators.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        maxWidth: '960px',
        margin: '0 auto',
        color: '#fff',
      }}
    >
      <div
        style={{
          border: '1px solid #474646',
          borderRadius: '16px',
          padding: '24px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Process Data</h2>
            {/* <p style={{ margin: '8px 0 0', color: '#B9B9B9', maxWidth: '680px', lineHeight: 1.5 }}>
              Run a processor against a selected tenant when long-running data needs to be rebuilt or refreshed.
            </p> */}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ border: '1px solid #474646', borderRadius: '999px', padding: '8px 14px', color: '#D7D7D7' }}>
              {processorTypes.length} processor types
            </div>
            <div style={{ border: '1px solid #474646', borderRadius: '999px', padding: '8px 14px', color: '#D7D7D7' }}>
              {tenants.length} tenants
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              border: '1px solid #2F2F32',
              borderRadius: '14px',
              padding: '18px',
              background: 'rgba(0, 0, 0, 0.16)',
            }}
          >
            <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: 600, marginBottom: '10px' }}>Processor Type</label>
            <p style={{ margin: '0 0 12px', color: '#9A9A9A', fontSize: '0.9rem' }}>
              Choose the processor that should run for the selected tenant.
            </p>
            <select
              value={selectedProcessor}
              onChange={(event) => setSelectedProcessor(event.target.value)}
              required
              disabled={isLoadingOptions || loading}
              style={{
                width: '100%',
                borderRadius: '10px',
                border: '1px solid #5A5A5F',
                background: '#1D1D1F',
                color: '#fff',
                padding: '12px 14px',
                outline: 'none',
              }}
            >
              <option value="">Select Processor Type</option>
              {processorTypes.map((processor) => (
                <option key={processor.value} value={processor.value}>
                  {processor.label}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              border: '1px solid #2F2F32',
              borderRadius: '14px',
              padding: '18px',
              background: 'rgba(0, 0, 0, 0.16)',
            }}
          >
            <label style={{ display: 'block', fontSize: '0.95rem', fontWeight: 600, marginBottom: '10px' }}>Tenant</label>
            <p style={{ margin: '0 0 12px', color: '#9A9A9A', fontSize: '0.9rem' }}>Select a tenant from the dashboard service.</p>
            <select
              value={selectedTenant}
              onChange={(event) => setSelectedTenant(event.target.value)}
              required
              disabled={isLoadingOptions || loading}
              style={{
                width: '100%',
                borderRadius: '10px',
                border: '1px solid #5A5A5F',
                background: '#1D1D1F',
                color: '#fff',
                padding: '12px 14px',
                outline: 'none',
              }}
            >
              <option value="">Select Tenant</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            borderTop: '1px solid #2F2F32',
            paddingTop: '20px',
          }}
        >
          <p style={{ margin: 0, color: '#9A9A9A', lineHeight: 1.5 }}>
            {isLoadingOptions ? 'Loading processor types and tenants.' : 'Review both selections before starting the process.'}
          </p>
          <Button
            label={loading ? 'Processing...' : 'Process Data'}
            isLoading={loading}
            disabled={loading || isLoadingOptions || !selectedProcessor || !selectedTenant}
          />
        </div>
      </div>
    </form>
  );
};

export default DataRefreshTab;
